import React, { useEffect, useRef, useState } from 'react';
import { X, MapPin, Calendar, CheckCircle2 } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useSession } from '../lib/session';
import { requireAuth } from './AuthGate';
import { claimMine, getMyBadge } from '../lib/pass';
import { centerToast } from './CenterToast';

const STASH_KEY = 'ihsanone_claim_pending';
const SG_TZ = 'Asia/Singapore';

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-SG', {
      timeZone: SG_TZ, weekday: 'short', day: 'numeric', month: 'short',
      year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

// A magic-link lands as https://ihsanone.org/?claim=1 (or #claim=1). The raw pass
// token is NOT in the URL — the claim is bound to the signed-in email server-side.
function hasClaimTrigger() {
  try {
    if (new URLSearchParams(window.location.search).get('claim')) return true;
    return /(?:^#|[?&])claim=/.test(window.location.hash || '');
  } catch { return false; }
}
function stripClaimFromUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('claim');
    const hash = (url.hash || '').replace(/^#/, '').replace(/(^|&)claim=[^&]*/, '').replace(/^&/, '');
    url.hash = hash;
    window.history.replaceState({}, '', url.toString());
  } catch { /* no-op */ }
}

/**
 * Claim entry point for the "email -> auto-logged-in -> badge in app" funnel.
 * Detects the ?claim trigger, ensures the member is signed in (reusing the
 * existing AuthGate), then binds their passes by email (qr_claim_mine) and shows
 * the resulting badge + scannable QR.
 */
export default function ClaimGate() {
  const session = useSession();
  const [result, setResult] = useState(null); // a qr_my_badge row
  const attemptedRef = useRef(false);
  const promptedRef = useRef(false);

  useEffect(() => {
    if (!hasClaimTrigger()) return;
    try { sessionStorage.setItem(STASH_KEY, '1'); } catch { /* private mode */ }
    stripClaimFromUrl();
  }, []);

  useEffect(() => {
    if (attemptedRef.current) return;
    let pending;
    try { pending = sessionStorage.getItem(STASH_KEY); } catch { pending = null; }
    if (!pending) return;

    if (!session) {
      if (promptedRef.current) return;
      promptedRef.current = true;
      requireAuth().then(() => { promptedRef.current = false; });
      return;
    }

    attemptedRef.current = true;
    (async () => {
      const r = await claimMine();
      try { sessionStorage.removeItem(STASH_KEY); } catch { /* no-op */ }
      const badges = await getMyBadge();
      if (Array.isArray(badges) && badges.length) {
        setResult(badges[0]);
      } else if (r && r.ok) {
        centerToast('You are signed in to IhsanOne.', '✨');
      } else {
        centerToast('Could not load your pass.', '⚠️');
      }
    })();
  }, [session]);

  if (!result) return null;

  const b = result;
  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
      role="dialog" aria-modal="true" aria-label="Your badge" data-testid="claim-badge-modal"
    >
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl relative overflow-hidden">
        <button
          type="button" aria-label="Close" data-testid="claim-badge-close"
          onClick={() => setResult(null)}
          className="absolute right-3 top-3 rounded-full p-1.5 bg-white/70 hover:bg-white text-muted-foreground hover:text-foreground transition-colors z-10"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-5" style={{ backgroundColor: '#0F4C4A' }}>
          <p className="text-xs font-semibold text-white/70 uppercase tracking-wider">Your badge</p>
          <h3 className="text-xl font-bold text-white mt-1 leading-tight">{b.program}</h3>
        </div>

        <div className="p-5 space-y-3">
          {b.checked_in && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Checked in
            </span>
          )}

          {b.badge_token && (
            <div className="flex flex-col items-center gap-2 py-1">
              <div className="bg-white p-3 rounded-xl border border-border">
                <QRCodeCanvas value={b.badge_token} size={188} level="M" marginSize={2} />
              </div>
              <p className="text-xs text-muted-foreground">Show this at the door</p>
            </div>
          )}

          {(b.first_name || b.table_no) && (
            <div className="rounded-xl border border-dashed border-border p-3 bg-muted/30 text-center">
              {b.first_name && <p className="text-sm font-semibold">{b.first_name}</p>}
              {b.table_no && <p className="text-xs text-muted-foreground mt-0.5">Table {b.table_no}</p>}
            </div>
          )}

          {(b.venue || b.starts_at) && (
            <div className="space-y-1.5 text-sm">
              {b.venue && (
                <p className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <span>{b.venue}{b.address ? ` · ${b.address}` : ''}</span>
                </p>
              )}
              {b.starts_at && (
                <p className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <span>{formatWhen(b.starts_at)} SGT</span>
                </p>
              )}
            </div>
          )}

          {b.ecert_body && (
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{b.ecert_body}</p>
          )}
        </div>
      </div>
    </div>
  );
}
